import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import { projectStockPoolApi, type ProjectPoolRow } from './projectStockPoolApi';

/**
 * Task 7.2 — commit units of a xotski from the brand catalogue to this project.
 *
 * This is the step that makes the middle rung real: until a project commits
 * units, its warehouse lane just mirrors the brand and two projects can quietly
 * promise the same stock. Committing draws a line around what this project may
 * spend.
 *
 * The number is the TOTAL the project may draw, not an amount to add — the
 * field opens on what the project already holds so a "+300" instinct reads as
 * "600 → 900", not "0 → 300".
 *
 * The backend enforces both ends (never below what the project's stations
 * already hold, never above that plus the brand's headroom); this dialog shows
 * the same range up front so the user is not told "no" after typing. Both
 * numbers come from the API — `allocated` from the pool list (also for stocks
 * the project holds without a commitment) and the headroom from the stock
 * list — so what is shown is what will be accepted.
 */
export function CommitToProjectDialog({
  open,
  onOpenChange,
  eventGroupId,
  stockId,
  stockName,
  brandFree,
  current,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventGroupId: number;
  stockId: number;
  stockName: string;
  /**
   * The brand's headroom from the API (`stock.headroom`): units free beyond
   * what every station already holds. null = untracked legacy stock.
   */
  brandFree: number | null;
  /**
   * The project's row for this stock: committed + allocated. Present also
   * when the stations hold the stock without a commitment (`adopted: false`).
   */
  current?: ProjectPoolRow;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('0');

  useEffect(() => {
    // Open on the larger of "committed" and "already at stations": the floor
    // is what the stations hold, and a commitment can never sit below it.
    if (open) setValue(String(Math.max(current?.committed ?? 0, current?.allocated ?? 0)));
  }, [open, current?.committed, current?.allocated]);

  const commit = useMutation({
    mutationFn: () => projectStockPoolApi.commit(eventGroupId, stockId, Number(value)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectStockPool.byProject(eventGroupId),
      });
      toast({
        tone: 'success',
        title: t('planner.pool.committed', { n: value, name: stockName }),
      });
      onOpenChange(false);
    },
    onError: (e) =>
      toast({ tone: 'error', title: t('planner.pool.commitFailed'), description: (e as Error).message }),
  });

  const n = Number(value);
  const floor = current?.allocated ?? 0;
  // Ceiling = what the stations hold + what the brand can still hand out.
  const ceiling = brandFree != null ? floor + brandFree : null;
  const tooLow = Number.isFinite(n) && n < floor;
  const tooHigh = ceiling != null && Number.isFinite(n) && n > ceiling;
  const invalid = !Number.isFinite(n) || n < 0 || tooLow || tooHigh;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('planner.pool.title', { name: stockName })}</DialogTitle>
          <DialogDescription>{t('planner.pool.description')}</DialogDescription>
        </DialogHeader>

        <FormField
          label={t('planner.pool.field')}
          hint={
            ceiling != null
              ? t('planner.pool.range', {
                  min: String(floor),
                  max: String(ceiling),
                  free: String(brandFree),
                })
              : t('planner.pool.atLeast', { min: String(floor) })
          }
          error={
            tooLow
              ? t('planner.pool.belowAllocated', { n: String(floor) })
              : tooHigh
                ? t('planner.pool.aboveBrand', { max: String(ceiling), free: String(brandFree) })
                : undefined
          }
        >
          <Input
            type="number"
            min={floor}
            max={ceiling ?? undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        </FormField>

        {current?.adopted && (
          <p className="text-xs text-[var(--color-fg-muted)]">
            {t('planner.pool.currently', {
              committed: String(current.committed),
              allocated: String(current.allocated),
              remaining: String(current.remaining),
            })}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={invalid} loading={commit.isPending} onClick={() => commit.mutate()}>
            {t('planner.pool.commit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
